import { useQuery } from '@tanstack/react-query'
import { useState, type MouseEvent, type ReactNode } from 'react'
import { ApiError } from '../api/client'
import {
  checkOneAlive,
  clearDiscovered,
  deployOne,
  discoveredCredentialsQuery,
  precheckOne,
  promoteDiscovered,
  removeOne,
  type DiscoveredCredential,
  type PromoteFields,
} from '../api/discoveredActions'
import type { DiscoveredPage, DiscoveredRow } from '../api/inventoree'
import { Modal } from '../ui/Modal'
import { toast } from '../ui/notify'
import { ConsolidateDuplicatesModal } from './discovered/ConsolidateDuplicatesModal'
import { readinessWords } from './discovered/readiness'

/**
 * Meniul din coloana Actions a listei Live Devices (`discoveryActionsForm()`)
 * si butoanele de pe toata lista ("Clear", "Consolidate duplicates").
 *
 * Celula vine gata facuta de la server, ca in portal, deci nu exista componente
 * React pe care sa se agate un `onClick`: exista `<a>`-uri cu clase (`deployOd`,
 * `precheckOd`, `moveToStatic`, `checkAlive`, `removeDiscovered`). Le prinde un
 * singur ascultator pe invelisul tabelului, dupa `closest()` - ca la Managed.
 *
 * Cele trei ferestre sunt cele din portal (`_deploy_one_modal.php`,
 * `_precheck_one_modal.php`, `_static_asset_modal.php`): deploy / proba cer un
 * cont din Pass Safe (parola nu pleaca de aici), iar "Add as unmanaged device"
 * vine cu tipul si marca propuse din adresa MAC si sediul din adresa, si spune
 * ca propunerea E propunere.
 */

function idOf(el: Element | null): number {
  const raw = el instanceof HTMLElement ? el.dataset.id : undefined
  const id = Number(raw)
  return Number.isFinite(id) && id > 0 ? id : 0
}

function failure(err: unknown): string {
  if (err instanceof ApiError) return err.message
  if (err instanceof Error) return err.message
  return 'Something went wrong'
}

function credentialLabel(c: DiscoveredCredential): string {
  const name = c.description.trim() || c.device_name.trim() || `#${c.id}`
  const user = c.user.trim()
  return user === '' ? name : `${name} - ${user}`
}

function Facts({ pairs }: { pairs: [string, string | null | undefined][] }) {
  return (
    <dl className="dl-horizontal" style={{ marginBottom: 12 }}>
      {pairs
        .filter(([, v]) => v !== null && v !== undefined && String(v).trim() !== '')
        .map(([k, v]) => (
          <span key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </span>
        ))}
    </dl>
  )
}

/** Alegerea contului din seif - `_credential_picker.php`. */
function CredentialPicker({ value, onChange, id }: { value: number; onChange(v: number): void; id: string }) {
  const vault = useQuery(discoveredCredentialsQuery)
  const credentials = vault.data?.credentials ?? []
  if (vault.isPending) return <p className="form-control-static">Loading Pass Safe...</p>
  if (credentials.length === 0) {
    return (
      <p className="form-control-static">
        <i className="fas fa-info-circle"></i> This company has no Pass Safe entry yet, and the agent cannot be installed
        without an account. <a href="/app/settings/pass_safe/index">Add one in Pass Safe</a>, then come back.
      </p>
    )
  }
  return (
    <>
      <select id={id} className="form-control" name="credential_id" value={value === 0 ? '' : String(value)} onChange={(e) => onChange(Number(e.target.value) || 0)}>
        <option value="">Select a credential...</option>
        {credentials.map((c) => (
          <option key={c.id} value={c.id}>
            {credentialLabel(c)}
          </option>
        ))}
      </select>
      <span className="help-block">
        <i className="fas fa-info-circle"></i> The password never leaves Pass Safe: the queued command carries only the
        entry, and the account is filled in when the agent picks it up.
      </span>
    </>
  )
}

type OneKind = 'deploy' | 'precheck'

export interface DiscoveredRowActions {
  onClick(event: MouseEvent<HTMLElement>): void
  /** Ferestrele; se pun o data, langa tabel. */
  modals: ReactNode
  /** "Clear" si "Consolidate duplicates" din bara tabelului. */
  listButtons: ReactNode
}

export function useDiscoveredRowActions(rows: DiscoveredRow[], page: DiscoveredPage | undefined, refresh: () => void): DiscoveredRowActions {
  const [one, setOne] = useState<{ kind: OneKind; row: DiscoveredRow } | null>(null)
  const [credentialId, setCredentialId] = useState(0)
  const [oneError, setOneError] = useState('')
  const [promoteRow, setPromoteRow] = useState<DiscoveredRow | null>(null)
  const [promote, setPromote] = useState<PromoteFields>({ device_name: '', device_type: '', device_brand: '', device_office: '' })
  const [promoteError, setPromoteError] = useState('')
  const [busy, setBusy] = useState(false)
  const [consolidating, setConsolidating] = useState(false)

  const rowOf = (el: Element): DiscoveredRow | undefined => rows.find((r) => Number(r.ID) === idOf(el))

  const onClick = (event: MouseEvent<HTMLElement>) => {
    const target = event.target
    if (!(target instanceof Element)) return

    const deploy = target.closest('a.deployOd')
    if (deploy) {
      event.preventDefault()
      const row = rowOf(deploy)
      if (row) {
        setCredentialId(0)
        setOneError('')
        setOne({ kind: 'deploy', row })
      }
      return
    }
    const precheck = target.closest('a.precheckOd')
    if (precheck) {
      event.preventDefault()
      const row = rowOf(precheck)
      if (row) {
        setCredentialId(0)
        setOneError('')
        setOne({ kind: 'precheck', row })
      }
      return
    }
    const promoteLink = target.closest('a.moveToStatic')
    if (promoteLink) {
      event.preventDefault()
      const row = rowOf(promoteLink)
      if (row) {
        setPromoteError('')
        setPromote({
          device_name: row.device_name || '',
          device_type: row.suggested_type ? String(row.suggested_type) : '',
          device_brand: row.suggested_brand ? String(row.suggested_brand) : '',
          device_office: row.suggested_office ? String(row.suggested_office) : '',
        })
        setPromoteRow(row)
      }
      return
    }
    const check = target.closest('a.checkAlive')
    if (check) {
      event.preventDefault()
      const id = idOf(check)
      if (id > 0) {
        void checkOneAlive(id)
          .then((r) => {
            if ((r.noRelay ?? 0) > 0) toast('This device has no relay to reach it.', 'Discovered', 'warning')
            else toast('Instruction sent.', 'Success')
            refresh()
          })
          .catch((err: unknown) => toast(failure(err), 'Error', 'error'))
      }
      return
    }
    const remove = target.closest('a.removeDiscovered')
    if (remove) {
      event.preventDefault()
      const id = idOf(remove)
      if (
        id > 0
        && window.confirm('Delete this device?\n\nIt is removed from the discovered list, not from the inventory. If it is still on the network, the next scan finds it again.')
      ) {
        void removeOne(id)
          .then(() => {
            toast('Device deleted.', 'Discovered')
            refresh()
          })
          .catch(() => toast('Could not delete.', 'Discovered', 'error'))
      }
    }
  }

  const submitOne = () => {
    if (one === null || busy || credentialId < 1) return
    setBusy(true)
    const id = Number(one.row.ID)
    const call = one.kind === 'deploy' ? deployOne(id, credentialId) : precheckOne(id, credentialId)
    void call
      .then(() => {
        setOne(null)
        toast(one.kind === 'deploy' ? 'Deploy Agent instruction sent.' : 'Check before deploy instruction sent.', 'Success')
        refresh()
      })
      .catch((err: unknown) => setOneError(failure(err) || 'The instruction could not be sent.'))
      .finally(() => setBusy(false))
  }

  const submitPromote = () => {
    if (promoteRow === null || busy) return
    setBusy(true)
    void promoteDiscovered(Number(promoteRow.ID), promote)
      .then(() => {
        setPromoteRow(null)
        toast('Added as an unmanaged device.', 'Success')
        refresh()
      })
      .catch((err: unknown) => setPromoteError(failure(err) || 'It could not be added.'))
      .finally(() => setBusy(false))
  }

  const clearAll = () => {
    if (!window.confirm('Are you sure?\n\nAll data will be lost.')) return
    void clearDiscovered()
      .then(() => {
        toast('List cleared.', 'Success')
        refresh()
      })
      .catch(() => toast('An error ocurred. Please try again later', 'Error', 'error'))
  }

  /* Fereastra cu optiuni si previzualizare (ConsolidateDuplicatesModal);
     aplicarea e gardata pe server (Super Admin) si refacuta acolo. */
  const dedupe = () => setConsolidating(true)

  const promoteLists = page?.promote ?? { types: [], brands: [], offices: [] }
  const promoteHint = promoteRow
    ? {
        type: promoteRow.suggested_type && !promoteRow.type ? 'suggested from the MAC address' : '',
        brand: promoteRow.suggested_brand
          ? 'suggested from the MAC address'
          : promoteRow.suggested_brand_name
            ? `looks like ${promoteRow.suggested_brand_name} - not in your brand list yet`
            : '',
        office: promoteRow.suggested_office ? `suggested from the address ${promoteRow.internal_ip || ''}` : '',
      }
    : { type: '', brand: '', office: '' }

  const modals = (
    <>
      <Modal
        id={one?.kind === 'precheck' ? 'precheckOneAgent' : 'deployOneAgent'}
        open={one !== null}
        onClose={() => setOne(null)}
        title={one?.kind === 'precheck' ? 'Check before deploy' : 'Deploy OptimumDesk'}
        footer={
          <>
            <button type="button" className="btn btn-success" disabled={busy || credentialId < 1} onClick={submitOne}>
              {one?.kind === 'precheck' ? 'Run check' : 'Deploy agent'}
            </button>
            <button type="button" className="btn btn-default" onClick={() => setOne(null)}>
              Close
            </button>
          </>
        }
      >
        {oneError !== '' && <div className="alert alert-danger">{oneError}</div>}
        {one !== null && (
          <Facts
            pairs={[
              ['Device', one.row.device_name],
              ['Address', one.row.internal_ip],
              [one.kind === 'precheck' ? 'From the last scan' : 'Install path', readinessWords(one.row.deploy_ready)],
            ]}
          />
        )}
        {one?.kind === 'precheck' && (
          <p className="text-muted">
            Answers "would the install work?" for this machine, without installing anything: address, the port the
            install would use, and one sign-in with this credential. It leaves a sign-in entry in the machine's log, and
            nothing else.
          </p>
        )}
        <form onSubmit={(e) => { e.preventDefault(); submitOne() }}>
          <div className="form-group">
            <label htmlFor="discovered-one-credential">Credential</label>
            <CredentialPicker id="discovered-one-credential" value={credentialId} onChange={setCredentialId} />
          </div>
        </form>
      </Modal>

      <Modal
        id="staticAsset"
        open={promoteRow !== null}
        onClose={() => setPromoteRow(null)}
        title="Add as unmanaged device"
        footer={
          <>
            <button type="button" className="btn btn-success saveStaticAsset" disabled={busy} onClick={submitPromote}>
              Add as unmanaged device
            </button>
            <button type="button" className="btn btn-default" onClick={() => setPromoteRow(null)}>
              Close
            </button>
          </>
        }
      >
        {promoteError !== '' && <div className="alert alert-danger" id="staticAssetError">{promoteError}</div>}
        {promoteRow !== null && (
          <Facts
            pairs={[
              ['Address', promoteRow.internal_ip],
              ['MAC', promoteRow.mac],
              ['Made by', promoteRow.manufacturer_name],
              ['Seen', promoteRow.last_saw],
            ]}
          />
        )}
        <form id="deviceAssetForm" onSubmit={(e) => { e.preventDefault(); submitPromote() }}>
          <div className="form-group">
            <label htmlFor="staticAssetName">Name</label>
            <input id="staticAssetName" type="text" name="device_name" className="form-control" maxLength={190} value={promote.device_name} onChange={(e) => setPromote({ ...promote, device_name: e.target.value })} />
          </div>
          <div className="form-group">
            <label htmlFor="staticAssetType">
              Device Type <span className="text-muted small" id="staticAssetTypeHint">{promoteHint.type}</span>
            </label>
            <select id="staticAssetType" name="device_type" className="form-control deviceType" value={promote.device_type} onChange={(e) => setPromote({ ...promote, device_type: e.target.value })}>
              <option value="">Select...</option>
              {promoteLists.types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="staticAssetBrand">
              Brand <span className="text-muted small" id="staticAssetBrandHint">{promoteHint.brand}</span>
            </label>
            <select id="staticAssetBrand" name="device_brand" className="form-control" value={promote.device_brand} onChange={(e) => setPromote({ ...promote, device_brand: e.target.value })}>
              <option value="">Not set</option>
              {promoteLists.brands.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="staticAssetOffice">Office</label>
            <select id="staticAssetOffice" name="device_office" className="form-control" value={promote.device_office} onChange={(e) => setPromote({ ...promote, device_office: e.target.value })}>
              <option value="">Not set</option>
              {promoteLists.offices.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <span className="text-muted small" id="staticAssetOfficeHint">{promoteHint.office}</span>
          </div>
        </form>
      </Modal>

      <ConsolidateDuplicatesModal open={consolidating} onClose={() => setConsolidating(false)} onDone={refresh} />
    </>
  )

  const listButtons = (
    <>
      <button className="btn btn-warning" id="discovery-dedupe" type="button" style={{ marginRight: 4 }} onClick={dedupe}>
        <i className="fas fa-compress-arrows-alt"></i> Consolidate duplicates
      </button>
      <button className="btn btn-danger" id="discovery-clear-all" type="button" style={{ marginRight: 4 }} onClick={clearAll}>
        <i className="fas fa-trash"></i> Clear
      </button>
    </>
  )

  return { onClick, modals, listButtons }
}
